import React, { useContext } from 'react';
import ActionMenuDropdown from '@/components/ActionMenuDropdown/ActionMenuDropdown';
import { AccountContext } from '@/context/account.context';
import { modalContext } from '@/context/modal.context';
import { toastContext } from '@/context/toast.context';
import { viewportContext } from '@/context/viewport.context';
import useMixpanel from '@/hooks/useMixpanel';
import resourceHelpers from '@/utils/helpers/resource';
import { copyShareLink, openFacebookShare, trackSharedResource } from '@/utils/sharer';
import useMarkUnavailable from '@/hooks/useMarkUnavailable';
import Submission from '@/utils/api/odas/submission';
import { isSuccessResponse } from '@/constants/httpStatus';
import t from '@/utils/Language/MoreActionsDropdown-translations';
import classes from './MoreActionsDropdown.module.scss';

const MoreActionsDropdown = ({ resource, buttonStyles, additionalClasses, hideUnavailableOption }) => {
  const account = useContext(AccountContext);
  const modal = useContext(modalContext);
  const { addToast } = useContext(toastContext);
  const { isXs } = useContext(viewportContext);
  const { openMarkUnavailableModal } = useMarkUnavailable();
  const { track } = useMixpanel();

  const isOpportunity = resource?.resource_type === 'Opportunity';
  const isAvailable = !resourceHelpers.isCurrentlyUnavailable(resource);

  const openAddToCollectionModal = () => {
    if (account?.state?.isLoggedIn) {
      modal.enqueueModal({ name: 'AddToCollectionModal', resource });
    } else {
      track('Signup prompted', { source: 'collections button', resource_id: resource.id, resource_type: resource.resource_type });
      modal.enqueueModal({ name: 'SignupModal' });
    }
  };

  // The five Edit-resource menu options track their click via the generic 'Clicked button' event,
  // grouped by category with the specific option in `value`.
  const trackEditResourceClick = (value) => {
    track('Clicked button', {
      category: 'Edit resource',
      value,
      source: 'action menu',
      resource_id: resource.id,
      resource_type: resource.resource_type
    });
  };

  const c = classes.menuItem;

  const menuList = [
    {
      key: 'share',
      icon: { style: 'fas', font: 'share-alt' },
      text: t('Share'),
      classes: c,
      children: [
        { key: 'share-back', back: true, icon: { style: 'fas', font: 'caret-left' }, text: t('Back'), classes: c },
        { key: 'email', icon: { style: 'fas', font: 'envelope' }, text: t('Email'), classes: c },
        { key: 'sms', icon: { style: 'fas', font: 'mobile-alt' }, text: t('Text message'), classes: c },
        { key: 'facebook', icon: { style: 'fab', font: 'facebook-square' }, text: t('Facebook'), classes: c },
        { key: 'copy', icon: { style: 'fas', font: 'link' }, text: t('Copy link'), classes: c }
      ]
    },
    {
      key: 'edit-resource',
      icon: { style: 'fas', font: 'pencil-alt' },
      text: t('Edit resource'),
      classes: c,
      children: [
        { key: 'edit-resource-back', back: true, icon: { style: 'fas', font: 'caret-left' }, text: t('Back'), classes: c },
        { key: 'make-edits', icon: { style: 'fas', font: 'pencil-alt' }, text: t('Make edits directly'), classes: c },
        { key: 'verify-information', icon: { style: 'fas', font: 'check-circle' }, text: t('Verify information'), classes: c },
        { key: 'inaccurate-information', icon: { style: 'fas', font: 'clipboard-question' }, text: t('Inaccurate information'), classes: c },
        {
          key: 'unavailable',
          icon: { style: 'fas', font: 'building-circle-xmark' },
          text: isAvailable ? t('Mark unavailable') : t('Mark available'),
          hidden: !isOpportunity || hideUnavailableOption,
          classes: c
        },
        { key: 'mark-deletion', icon: { style: 'fas', font: 'trash-alt' }, text: t('Mark for deletion'), classes: c }
      ]
    },
    // Collections has its own dedicated button on desktop -- only surface it here on mobile.
    { key: 'collections', icon: { style: 'fas', font: 'book' }, text: t('Collections'), hidden: !isXs, classes: c },
    { key: 'print', icon: { style: 'fas', font: 'file-pdf' }, text: t('Download PDF'), classes: c }
  ];

  const onSelect = (eventKey) => {
    switch (eventKey) {
      case 'email':
      case 'sms':
        modal.enqueueModal({ name: 'ShareResourceModal', method: eventKey, resource });
        break;
      case 'facebook':
        trackSharedResource(track, resource, eventKey);
        openFacebookShare();
        break;
      case 'copy':
        trackSharedResource(track, resource, eventKey);
        if (copyShareLink(resource)) {
          addToast({ message: t('Link copied to your clipboard.'), delay: 3000, type: 'success' });
        }
        break;
      case 'make-edits':
        trackEditResourceClick('make edits');
        window.location.href = `${resourceHelpers.pathTo(resource)}/edit`;
        break;
      case 'verify-information':
        trackEditResourceClick('verify information');
        modal.enqueueModal(
          {
            name: 'ConfirmationModal',
            title: t('Verify information'),
            text: t('Do you verify all of the information on this page is correct?'),
            confirmButtonText: t('Yes'),
            declineButtonText: t('Not sure'),
            onConfirm: async () => {
              try {
                const response = await Submission.verifyResource({ resource });
                if (isSuccessResponse(response.status)) {
                  track('Verified information', { source: 'action menu', resource_id: resource.id, resource_type: resource.resource_type });
                  addToast({ message: t('Thank you for verifying that this information is correct!'), type: 'success' });
                } else {
                  addToast({ message: t('There was a problem with your submission_'), type: 'error' });
                }
              } catch (error) {
                addToast({ message: t('There was a problem with your submission_'), type: 'error' });
              }
            }
          },
          { forceOpen: true }
        );
        break;
      case 'inaccurate-information':
        trackEditResourceClick('inaccurate information');
        modal.enqueueModal({ name: 'InaccurateInformationModal', resource });
        break;
      case 'unavailable':
        trackEditResourceClick(isAvailable ? 'mark unavailable' : 'mark available');
        openMarkUnavailableModal(resource);
        break;
      case 'mark-deletion':
        trackEditResourceClick('mark for deletion');
        modal.enqueueModal(
          {
            name: 'ConfirmationModal',
            title: t('Mark for deletion'),
            text: t('Are you sure you want to mark this resource for deletion?'),
            confirmButtonText: t('Yes, mark for deletion'),
            confirmButtonType: 'destructive',
            declineButtonText: t('No'),
            onConfirm: async () => {
              try {
                const response = await Submission.requestResourceDeletion({
                  resource,
                  deletionInfoHash: {
                    is_duplicate: false,
                    is_permanently_unavailable: false,
                    deletion_more_information: '',
                    fetchable_type: resource?.resource_type,
                    fetchable_id: resource?.id
                  }
                });
                if (isSuccessResponse(response.status)) {
                  track('Marked resource for deletion', { source: 'action menu', resource_id: resource.id, resource_type: resource.resource_type });
                  addToast({ message: t("Thank you for submitting a deletion request. We'll update this as quickly as possible_"), type: 'success' });
                } else {
                  addToast({ message: t('There was a problem with your submission_'), type: 'error' });
                }
              } catch (error) {
                addToast({ message: t('There was a problem with your submission_'), type: 'error' });
              }
            }
          },
          { forceOpen: true }
        );
        break;
      case 'collections':
        openAddToCollectionModal();
        break;
      case 'print': {
        // Opportunity print tracking mirrors Angular's PdfHelpers.trackingInfo('opportunity') +
        // Pdf.generatePdf; PrintModal fires the events once the PDF is ready. Passed only for
        // opportunities so other resource types don't emit opportunity-shaped events.
        const trackingInfo =
          resource.resource_type === 'Opportunity'
            ? {
                type: 'opportunity',
                source: 'opportunity',
                event: 'Downloaded opportunity PDF',
                message: 'single',
                object_type: 'Opportunity',
                object_id: resource.id,
                via: 'button on opportunity page'
              }
            : undefined;
        modal.enqueueModal({
          name: 'PrintModal',
          title: t('Generating PDF...'),
          is_referral: false,
          resources: [{ id: resource.id }],
          ...(trackingInfo && { resource, trackingInfo })
        });
        break;
      }
    }
  };

  return (
    <ActionMenuDropdown
      buttonStyles={
        buttonStyles || {
          buttonType: 'link',
          buttonShape: 'circle',
          buttonIcon: { style: 'fas', font: 'ellipsis' }
        }
      }
      additionalClasses={additionalClasses}
      menuList={menuList}
      onSelect={onSelect}
    />
  );
};

export default MoreActionsDropdown;
